import React, { useState, useEffect, useRef } from 'react'
import { Brain, Send, Trash2, ToggleLeft, ToggleRight, AlertTriangle, CheckCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

const CATEGORIES = [
  { value: 'analyse', label: 'Analyse IA', desc: 'Erreur dans l\'analyse du sinistre' },
  { value: 'redaction', label: 'Rédaction', desc: 'Erreur de formulation ou de ton' },
  { value: 'classification', label: 'Classification', desc: 'Mauvaise catégorisation' },
  { value: 'general', label: 'Général', desc: 'Autre type d\'erreur' },
]

const CATEGORY_COLORS = {
  analyse: 'bg-blue-50 text-blue-700 border-blue-200',
  redaction: 'bg-violet-50 text-violet-700 border-violet-200',
  classification: 'bg-amber-50 text-amber-700 border-amber-200',
  general: 'bg-slate-50 text-slate-700 border-slate-200',
}

const EXAMPLE_RULE = {
  categorie: 'analyse',
  regle: 'Ne jamais classer un dégât des eaux comme "incendie" même si le feu a été causé par un court-circuit lié à l\'eau.',
  contexte: 'Le C1 avait classé le sinistre #SIN-2024-0451 comme incendie alors que c\'était un dégât des eaux avec court-circuit secondaire.',
}

export default function C1MemoirePanel({ tenantId }) {
  const [memories, setMemories] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showExample, setShowExample] = useState(true)
  const [form, setForm] = useState({ categorie: 'analyse', regle: '', contexte: '' })
  const bottomRef = useRef(null)

  useEffect(() => {
    if (tenantId) fetchMemories()
  }, [tenantId])

  async function fetchMemories() {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/c1/memoire/${tenantId}?active_only=false`)
      const data = await res.json()
      setMemories(data.memories || [])
    } catch {
      toast.error('Erreur chargement mémoire C1')
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.regle.trim()) {
      toast.error('Décrivez la correction attendue')
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch(`${API_URL}/api/c1/memoire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId, ...form }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Correction enregistrée')
        setForm({ categorie: 'analyse', regle: '', contexte: '' })
        setShowExample(false)
        fetchMemories()
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 200)
      } else {
        toast.error(data.detail || 'Erreur')
      }
    } catch {
      toast.error('Erreur réseau')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleToggle(id, currentActive) {
    try {
      const res = await fetch(`${API_URL}/api/c1/memoire/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success(currentActive ? 'Règle désactivée' : 'Règle réactivée')
        fetchMemories()
      }
    } catch {
      toast.error('Erreur')
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer définitivement cette correction ?')) return
    try {
      await fetch(`${API_URL}/api/c1/memoire/${id}`, { method: 'DELETE' })
      toast.success('Correction supprimée')
      fetchMemories()
    } catch {
      toast.error('Erreur')
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400" data-testid="c1-memoire-loading">
        <Loader2 size={20} className="animate-spin mr-2" />
        Chargement...
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="c1-memoire-panel">
      {/* Header explanation */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Brain size={16} className="text-orange-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-orange-800">Mémoire d'apprentissage C1</h3>
            <p className="text-xs text-orange-600 mt-1 leading-relaxed">
              Signalez les erreurs de l'IA pour qu'elle ne les répète plus. Chaque correction est injectée automatiquement dans les futures analyses et rédactions.
            </p>
          </div>
        </div>
      </div>

      {/* Example card - collapsible */}
      <div className="border border-dashed border-slate-300 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowExample(!showExample)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
          data-testid="toggle-example-btn"
        >
          <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
            <AlertTriangle size={12} />
            Exemple de signalement
          </span>
          {showExample ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
        </button>
        {showExample && (
          <div className="px-4 py-3 bg-white space-y-2">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${CATEGORY_COLORS[EXAMPLE_RULE.categorie]}`}>
                {CATEGORIES.find(c => c.value === EXAMPLE_RULE.categorie)?.label}
              </span>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg p-3">
              <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider mb-1">Erreur constatée</p>
              <p className="text-xs text-red-700">{EXAMPLE_RULE.contexte}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <p className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider mb-1">Correction à appliquer</p>
              <p className="text-xs text-emerald-700">{EXAMPLE_RULE.regle}</p>
            </div>
          </div>
        )}
      </div>

      {/* Existing rules list (chat-like) */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="c1-memoire-rules-list">
        {memories.length === 0 && (
          <p className="text-center text-xs text-slate-400 py-6">
            Aucune correction enregistrée. Utilisez le formulaire ci-dessous pour signaler une erreur.
          </p>
        )}
        {memories.map((m) => (
          <div
            key={m.id}
            className={`rounded-xl border p-3 transition-all ${
              m.active
                ? 'bg-white border-slate-200 shadow-sm'
                : 'bg-slate-50 border-slate-100 opacity-60'
            }`}
            data-testid={`c1-memoire-rule-${m.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${CATEGORY_COLORS[m.categorie] || CATEGORY_COLORS.general}`}>
                    {CATEGORIES.find(c => c.value === m.categorie)?.label || m.categorie}
                  </span>
                  {m.active ? (
                    <CheckCircle size={12} className="text-emerald-500" />
                  ) : (
                    <span className="text-[10px] text-slate-400 italic">désactivée</span>
                  )}
                  <span className="text-[10px] text-slate-400 ml-auto">{formatDate(m.created_at)}</span>
                </div>

                {m.contexte && (
                  <div className="bg-red-50/60 border border-red-100 rounded-lg p-2 mb-1.5">
                    <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wider mb-0.5">Erreur signalée</p>
                    <p className="text-xs text-red-700 leading-relaxed">{m.contexte}</p>
                  </div>
                )}

                <div className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-2">
                  <p className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-0.5">Correction</p>
                  <p className="text-xs text-emerald-700 leading-relaxed">{m.regle}</p>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleToggle(m.id, m.active)}
                  className="p-1 rounded hover:bg-slate-100 transition-colors"
                  title={m.active ? 'Désactiver' : 'Réactiver'}
                  data-testid={`toggle-rule-${m.id}`}
                >
                  {m.active ? (
                    <ToggleRight size={16} className="text-emerald-500" />
                  ) : (
                    <ToggleLeft size={16} className="text-slate-400" />
                  )}
                </button>
                <button
                  onClick={() => handleDelete(m.id)}
                  className="p-1 rounded hover:bg-red-50 transition-colors"
                  title="Supprimer"
                  data-testid={`delete-rule-${m.id}`}
                >
                  <Trash2 size={14} className="text-slate-300 hover:text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input form - chat-like */}
      <form onSubmit={handleSubmit} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3" data-testid="c1-memoire-form">
        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
          <AlertTriangle size={12} className="text-orange-500" />
          Signaler une erreur C1
        </h4>

        {/* Category */}
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map(cat => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setForm(prev => ({ ...prev, categorie: cat.value }))}
              className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${
                form.categorie === cat.value
                  ? CATEGORY_COLORS[cat.value]
                  : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`cat-${cat.value}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Error context */}
        <div>
          <label className="block text-[10px] text-slate-500 mb-1 font-medium">
            Quelle erreur avez-vous constatée ?
          </label>
          <textarea
            value={form.contexte}
            onChange={(e) => setForm(prev => ({ ...prev, contexte: e.target.value }))}
            placeholder="Ex: Le C1 a classé le sinistre SIN-2024-0451 comme incendie alors que c'était un dégât des eaux..."
            rows={2}
            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 resize-none bg-white"
            data-testid="c1-memoire-contexte-input"
          />
        </div>

        {/* Correction */}
        <div>
          <label className="block text-[10px] text-slate-500 mb-1 font-medium">
            Quelle est la correction attendue ? *
          </label>
          <textarea
            value={form.regle}
            onChange={(e) => setForm(prev => ({ ...prev, regle: e.target.value }))}
            placeholder="Ex: Ne jamais classer un dégât des eaux comme incendie même si un court-circuit est impliqué."
            rows={2}
            required
            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 resize-none bg-white"
            data-testid="c1-memoire-regle-input"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting || !form.regle.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white text-xs font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="c1-memoire-submit-btn"
          >
            {submitting ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Send size={12} />
            )}
            Enregistrer la correction
          </button>
        </div>
      </form>
    </div>
  )
}
